import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Pagination,
    InputAdornment,
    Avatar,
    IconButton,
    Chip,
    CircularProgress,
    Select,
    MenuItem,
    Divider
} from "@mui/material";

import { FaSearch, FaTrash, FaExternalLinkAlt, FaCrown, FaLayerGroup } from "react-icons/fa";

import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";

type Club = {
    id: string;
    name: string;
    subscriberCount?: number;
    status?: string;
    creatorName?: string
};

function ClubsPage() {

    const [ clubs, setClubs ] = useState<Club[]>([]);
    const [ loading, setLoading ] = useState(false);

    const [ searchInput, setSearchInput ] = useState("");
    const [ searchQuery, setSearchQuery ] = useState("");

    const [ page, setPage ] = useState(1);
    const [ itemsPerPage, setItemsPerPage ] = useState(8);

    const [ totalItems, setTotalItems ] = useState(0);

    const [ cursors, setCursors ] = useState<(string | null)[]>([ null ]);

    const fetchClubs = async () => {

        try {

            setLoading(true);

            const cursor = cursors[ page - 1 ] || null;

            const res = await fetch(
                `${environment.BASE_URL}/clubs/getPaginatedClubs`,
                {
                    method: "POST",
                    headers: apiHeaders(),
                    body: JSON.stringify({
                        cursor,
                        pageSize: itemsPerPage,
                        searchText: searchQuery
                    })
                }
            );

            const result = await res.json();

            if (!result.success) return;

            setClubs(result.data || []);

            if (result.totalCount !== null && result.totalCount !== undefined) {
                setTotalItems(result.totalCount);
            }

            if (result.nextCursor) {
                setCursors(prev => {
                    const copy = [ ...prev ];
                    copy[ page ] = result.nextCursor;
                    return copy;
                });
            }

        } catch (err) {

            console.error("Fetch clubs failed", err);

        } finally {

            setLoading(false);

        }
    };

    useEffect(() => {
        fetchClubs();
    }, [ page, searchQuery, itemsPerPage ]);

    const totalPages = Math.max(
        Math.ceil(totalItems / itemsPerPage),
        page
    );

    const handlePageSizeChange = (event: any) => {

        const newSize = event.target.value;

        setItemsPerPage(newSize);

        setPage(1);

        setCursors([ null ]);

    };

    const handleDelete = (id: string) => {

        setClubs(prev => prev.filter(c => c.id !== id));

    };

    return (

        <Box sx={{ minHeight: "100vh", bgcolor: "oklch(0.97 0.01 90)", p: 4 }}>

            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#3e2f25" }}>
                        Club Directory
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6d5c4f" }}>
                        Review and manage all registered campus organizations
                    </Typography>
                </Box>

                <Chip
                    icon={<FaLayerGroup size={12} style={{ marginLeft: 8 }} />}
                    label={`Total Found: ${totalItems}`}
                    sx={{ bgcolor: "#ffcb77", color: "#3e2f25", fontWeight: "bold" }}
                />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>

                <TextField
                    sx={{ flex: 1 }}
                    placeholder="Search name of club"
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            setSearchQuery(searchInput);
                            setPage(1);
                            setCursors([ null ]);
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <FaSearch color="#8a7c6e" />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: "12px", bgcolor: "#fff" }
                    }}
                />

                <Button
                    variant="contained"
                    onClick={() => {
                        setSearchQuery(searchInput);
                        setPage(1);
                        setCursors([ null ]);
                    }}
                    sx={{
                        bgcolor: "#ffcb77",
                        color: "#3e2f25",
                        fontWeight: "bold",
                        "&:hover": { bgcolor: "#ffb94f" }
                    }}
                >
                    Search
                </Button>

            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: "14px", border: "1px solid #eadfce", position: 'relative' }}>

                {loading && (

                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.7)'
                    }}>
                        <CircularProgress size={40} sx={{ color: '#ffcb77' }} />
                    </Box>

                )}

                <Table>

                    <TableHead sx={{ bgcolor: "#f8f4ec" }}>
                        <TableRow>
                            <TableCell>Club Name</TableCell>
                            <TableCell>Lead / Owner</TableCell>
                            <TableCell>Members</TableCell>
                            {/*<TableCell>Status</TableCell>*/}
                            {/*<TableCell align="right">Actions</TableCell>*/}
                        </TableRow>
                    </TableHead>

                    <TableBody>

                        {clubs.length === 0 && !loading ? (

                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No clubs found.
                                </TableCell>
                            </TableRow>

                        ) : (

                            clubs.map((club) => (

                                <TableRow key={club.id} hover>

                                    <TableCell>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

                                            <Avatar sx={{ bgcolor: "#3e2f25", color: "#ffcb77" }}>
                                                {club.name?.charAt(0) ?? "?"}
                                            </Avatar>

                                            <Box>
                                                <Typography fontWeight="700">
                                                    {club.name}
                                                </Typography>
                                                <Typography variant="caption">
                                                    ID: {club.id}
                                                </Typography>
                                            </Box>

                                        </Box>

                                    </TableCell>

                                    <TableCell>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <FaCrown size={12} />
                                            <Typography variant="body2">
                                                {club.creatorName ?? "—"}
                                            </Typography>
                                        </Box>

                                    </TableCell>

                                    <TableCell>
                                        {club.subscriberCount ?? 0}
                                    </TableCell>

                                    {/* 
                                    <TableCell>

                                        <Chip
                                            label={club.status ?? "Active"}
                                            size="small"
                                        />

                                    </TableCell>
                                    */}
                                    {/* 
                                    <TableCell align="right">

                                        <IconButton size="small">
                                            <FaExternalLinkAlt size={14} />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(club.id)}
                                            sx={{ color: "#c62828" }}
                                        >
                                            <FaTrash size={14} />
                                        </IconButton>

                                    </TableCell>
                                    */}

                                </TableRow>

                            ))

                        )}

                    </TableBody>

                </Table>

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <Typography sx={{ fontSize: '0.75rem' }}>
                        SHOWING {clubs.length > 0 ? (page - 1) * itemsPerPage + 1 : 0} - {Math.min(page * itemsPerPage, totalItems)} OF {totalItems}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2 }}>

                        <Select
                            value={itemsPerPage}
                            onChange={handlePageSizeChange}
                            size="small"
                            variant="outlined"
                            sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                bgcolor: '#fff',
                                '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #eadfce' }
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={8}>8</MenuItem>
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={30}>30</MenuItem>
                        </Select>

                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                            size="small"
                            sx={{
                                "& .MuiPaginationItem-root.Mui-selected": {
                                    bgcolor: "#ffcb77",
                                    color: "#3e2f25",
                                    fontWeight: 'bold',
                                    "&:hover": { bgcolor: "#ffb94f" }
                                }
                            }}
                        />

                    </Box>

                </Box>

            </TableContainer>

        </Box>
    );
}

export default ClubsPage;